import { Head, useForm, Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowLeft, Save } from "lucide-react"

interface Patient {
  id?: string
  first_name: string
  last_name: string
  birth_date: string
  address: string
  postal_code: string
  city: string
  phone: string
  email: string

  attending_physician: string
  occupation: string
  number_of_children: number
  children_info: string
  height: number
  weight: number
  lifestyle_alcohol: boolean
  lifestyle_tobacco: boolean
  lifestyle_coffee: boolean
  lifestyle_activity: string
  family_history: string[]
  personal_history: string[]
  other_history: string
  intoxication: string
  allergies: string
  vaccinations: string
  medical_history: string
  medications: string
}

interface FormProps {
  patient?: Patient
}

export default function Form({ patient }: FormProps) {
  const isEditing = !!patient?.id

  const { data, setData, post, put, processing, errors } = useForm({
    first_name: patient?.first_name || "",
    last_name: patient?.last_name || "",
    birth_date: patient?.birth_date ? new Date(patient.birth_date).toISOString().split('T')[0] : "",
    address: patient?.address || "",
    postal_code: patient?.postal_code || "",
    city: patient?.city || "",
    phone: patient?.phone || "",
    email: patient?.email || "",
    attending_physician: patient?.attending_physician || "",
    occupation: patient?.occupation || "",
    number_of_children: patient?.number_of_children || 0,
    children_info: patient?.children_info || "",
    height: patient?.height || "",
    weight: patient?.weight || "",
    lifestyle_alcohol: patient?.lifestyle_alcohol || false,
    lifestyle_tobacco: patient?.lifestyle_tobacco || false,
    lifestyle_coffee: patient?.lifestyle_coffee || false,
    lifestyle_activity: patient?.lifestyle_activity || "",
    family_history: patient?.family_history || [],
    personal_history: patient?.personal_history || [],
    other_history: patient?.other_history || "",
    intoxication: patient?.intoxication || "",
    allergies: patient?.allergies || "",
    vaccinations: patient?.vaccinations || "",
    medical_history: patient?.medical_history || "",
    medications: patient?.medications || "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditing) {
      put(`/patients/${patient!.id}`)
    } else {
      post("/patients")
    }
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Head title={isEditing ? "Modifier Patient" : "Nouveau Patient"} />

      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" asChild>
          <Link href={isEditing ? `/patients/${patient!.id}` : "/patients"}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">
          {isEditing ? "Modifier Patient" : "Nouveau Patient"}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Identité */}
          <Card>
            <CardHeader>
              <CardTitle>Identité</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom</Label>
                <Input
                  id="first_name"
                  value={data.first_name}
                  onChange={(e) => setData("first_name", e.target.value)}
                  required
                />
                {errors.first_name && <p className="text-sm text-red-500">{errors.first_name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom</Label>
                <Input
                  id="last_name"
                  value={data.last_name}
                  onChange={(e) => setData("last_name", e.target.value)}
                  required
                />
                {errors.last_name && <p className="text-sm text-red-500">{errors.last_name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="birth_date">Date de naissance</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={data.birth_date}
                  onChange={(e) => setData("birth_date", e.target.value)}
                  required
                />
                {errors.birth_date && <p className="text-sm text-red-500">{errors.birth_date}</p>}
              </div>

            </CardContent>
          </Card>

          {/* Coordonnées */}
          <Card>
            <CardHeader>
              <CardTitle>Coordonnées</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  value={data.address}
                  onChange={(e) => setData("address", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">Code Postal</Label>
                <Input
                  id="postal_code"
                  value={data.postal_code}
                  onChange={(e) => setData("postal_code", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={data.city}
                  onChange={(e) => setData("city", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => setData("phone", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Informations Complémentaires */}
          <Card>
            <CardHeader>
              <CardTitle>Informations Complémentaires</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="attending_physician">Médecin traitant</Label>
                <Input
                  id="attending_physician"
                  value={data.attending_physician}
                  onChange={(e) => setData("attending_physician", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="occupation">Profession</Label>
                <Input
                  id="occupation"
                  value={data.occupation}
                  onChange={(e) => setData("occupation", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="number_of_children">Nombre d'enfants</Label>
                <Input
                  id="number_of_children"
                  type="number"
                  min="0"
                  value={data.number_of_children}
                  onChange={(e) => setData("number_of_children", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="children_info">Dates de naissance enfants / Infos</Label>
                <Textarea
                  id="children_info"
                  value={data.children_info}
                  onChange={(e) => setData("children_info", e.target.value)}
                  placeholder="Ex: 2010, 2012..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Mesures & Mode de vie */}
          <Card>
            <CardHeader>
              <CardTitle>Mesures & Mode de vie</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="height">Taille (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    value={data.height}
                    onChange={(e) => setData("height", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Poids actuel (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    value={data.weight}
                    onChange={(e) => setData("weight", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label>Mode de vie</Label>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="lifestyle_alcohol" 
                      checked={data.lifestyle_alcohol}
                      onCheckedChange={(checked: boolean) => setData("lifestyle_alcohol", checked)}
                    />
                    <Label htmlFor="lifestyle_alcohol">Alcool</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="lifestyle_tobacco" 
                      checked={data.lifestyle_tobacco}
                      onCheckedChange={(checked: boolean) => setData("lifestyle_tobacco", checked)}
                    />
                    <Label htmlFor="lifestyle_tobacco">Tabac</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="lifestyle_coffee" 
                      checked={data.lifestyle_coffee}
                      onCheckedChange={(checked: boolean) => setData("lifestyle_coffee", checked)}
                    />
                    <Label htmlFor="lifestyle_coffee">Café</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lifestyle_activity">Activité physique</Label>
                <Input
                  id="lifestyle_activity"
                  value={data.lifestyle_activity}
                  onChange={(e) => setData("lifestyle_activity", e.target.value)}
                  placeholder="Ex: Marche, Sport..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Antécédents & Historique */}
          <Card>
            <CardHeader>
              <CardTitle>Antécédents & Historique</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <Label>Antécédents familiaux</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      "attaque cérébrale", "troubles cardio-vasculaires", "asthme, eczéma", "goutte", "allergies",
                      "diabète", "obésité", "maladie du sang", "troubles neuropsychiques", "maladies infectieuses",
                      "arthrose", "arthrite", "cancers"
                    ].map((item) => (
                      <div key={item} className="flex items-center space-x-2">
                        <Checkbox
                          id={`family_${item}`}
                          checked={data.family_history.includes(item)}
                          onCheckedChange={(checked) => {
                            const current = data.family_history
                            const next = checked
                              ? [...current, item]
                              : current.filter((i) => i !== item)
                            setData("family_history", next)
                          }}
                        />
                        <Label htmlFor={`family_${item}`} className="font-normal text-sm">{item}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>Antécédents personnels (0 à 10 ans)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      "rougeole", "scarlatine", "coqueluche", "varicelle", "rubéole",
                      "oreillons", "angine", "rhumes", "grippes", "bronchites",
                      "otites", "asthme", "eczéma"
                    ].map((item) => (
                      <div key={item} className="flex items-center space-x-2">
                        <Checkbox
                          id={`personal_${item}`}
                          checked={data.personal_history.includes(item)}
                          onCheckedChange={(checked) => {
                            const current = data.personal_history
                            const next = checked
                              ? [...current, item]
                              : current.filter((i) => i !== item)
                            setData("personal_history", next)
                          }}
                        />
                        <Label htmlFor={`personal_${item}`} className="font-normal text-sm">{item}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="other_history">Autres antécédents</Label>
                <Input
                  id="other_history"
                  value={data.other_history}
                  onChange={(e) => setData("other_history", e.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="medical_history">Passé médical (maladies, opérations, accidents)</Label>
                  <Textarea
                    id="medical_history"
                    value={data.medical_history}
                    onChange={(e) => setData("medical_history", e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medications">Médicaments actuels et compléments</Label>
                  <Textarea
                    id="medications"
                    value={data.medications}
                    onChange={(e) => setData("medications", e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="allergies">Allergies</Label>
                  <Input
                    id="allergies"
                    value={data.allergies}
                    onChange={(e) => setData("allergies", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vaccinations">Vaccinations</Label>
                  <Input
                    id="vaccinations"
                    value={data.vaccinations}
                    onChange={(e) => setData("vaccinations", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intoxication">Intoxication</Label>
                  <Input
                    id="intoxication"
                    value={data.intoxication}
                    onChange={(e) => setData("intoxication", e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={processing}>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? "Mettre à jour" : "Créer le patient"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
